import { retrieveAccessTokenFromLocalStorage } from "@/utils/utils-dependent";

export const authHeaders = () => {
  const token = retrieveAccessTokenFromLocalStorage()?.accessToken;
  if (!token) {
    throw new Error(
      "No valid access token found. Refresh the page or sign in again."
    );
  }
  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json"
  };
};

type HasuraActionResponse = {
  status: number;
  body: string;
};

const actionDocuments = {
  aiDosAgentCoreAdmin: `mutation AiDosAgentCoreAdmin($input: AiDosAgentCoreAdminInput!) {
    aiDosAgentCoreAdmin(input: $input) {
      status
      body
    }
  }`,
  aiDosCompletedSizings: `mutation AiDosCompletedSizings($input: AiDosCompletedSizingsInput!) {
    aiDosCompletedSizings(input: $input) {
      status
      body
    }
  }`,
  aiDosErpMaterialMappingAutomation: `mutation AiDosErpMaterialMappingAutomation($input: AiDosErpMaterialMappingAutomationInput!) {
    aiDosErpMaterialMappingAutomation(input: $input) {
      status
      body
    }
  }`,
  aiDosGenerateCandidates: `mutation AiDosGenerateCandidates($input: AiDosGenerateCandidatesInput!) {
    aiDosGenerateCandidates(input: $input) {
      status
      body
    }
  }`,
  aiDosMaterialSearch: `mutation AiDosMaterialSearch($input: AiDosMaterialSearchInput!) {
    aiDosMaterialSearch(input: $input) {
      status
      body
    }
  }`,
  aiDosResolveErpMaterialMappings: `mutation AiDosResolveErpMaterialMappings($input: AiDosResolveErpMaterialMappingsInput!) {
    aiDosResolveErpMaterialMappings(input: $input) {
      status
      body
    }
  }`,
  aiDosRunSizingCalculation: `mutation AiDosRunSizingCalculation($input: AiDosRunSizingCalculationInput!) {
    aiDosRunSizingCalculation(input: $input) {
      status
      body
    }
  }`,
  aiDosSizingReportContext: `mutation AiDosSizingReportContext($input: AiDosSizingReportContextInput!) {
    aiDosSizingReportContext(input: $input) {
      status
      body
    }
  }`,
  aiDosSizingTrace: `mutation AiDosSizingTrace($input: AiDosSizingTraceInput!) {
    aiDosSizingTrace(input: $input) {
      status
      body
    }
  }`
};

const actionMutation = (name: keyof typeof actionDocuments) =>
  actionDocuments[name];

const parseActionBody = (body: string) => {
  try {
    return JSON.parse(body);
  } catch {
    return { message: body };
  }
};

export const executeAction = async <T>(
  name: keyof typeof actionDocuments,
  input: Record<string, unknown>
) => {
  const response = await fetch(window.config.GRAPHQL_URL, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify({
      query: actionMutation(name),
      variables: { input }
    })
  });
  const payload = await response.json();
  if (!response.ok || payload.errors?.length) {
    const message = payload.errors?.[0]?.message || JSON.stringify(payload);
    const apiError = new Error(
      `${response.status} ${response.statusText}: ${message}`
    );
    Object.assign(apiError, { status: response.status, payload });
    throw apiError;
  }

  const actionResponse = payload.data?.[name] as
    | HasuraActionResponse
    | undefined;
  if (!actionResponse) {
    throw new Error(`Hasura action ${name} returned no response.`);
  }
  const body = parseActionBody(actionResponse.body);
  if (actionResponse.status >= 400) {
    const apiError = new Error(
      `${actionResponse.status}: ${body.message || JSON.stringify(body)}`
    );
    Object.assign(apiError, { status: actionResponse.status, payload: body });
    throw apiError;
  }
  return body as T;
};
